import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { VersionInfoDto } from '@common-modules/navigation/models/version-info.dto';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { ObjectHelperService } from '@common-modules/shared/helpers/object-helper.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { VersionInfoService } from '@water-loss/features/navigation/version-info-popup/version-info.service';
import { environment } from 'src/environments/environment';
import { cloudTwinLicenseModules } from '../../../../../cloud-twin/src/app/cloud-twin-app-license-modules';
import { cloudTwinAppNavigations } from '../../../../../cloud-twin/src/app/cloud-twin-navigations';

const COMPONENT_SELECTOR = 'wlm-version-info-popup';

@UntilDestroy()
@Component({
  selector: 'wl-version-info-popup',
  templateUrl: './version-info-popup.component.html',
  styleUrls: ['./version-info-popup.component.scss'],
  standalone: false,
})
export class VersionInfoPopupComponent implements OnInit {
  T_SCOPE = `${TranslateScopes.WlmNavigation}.${COMPONENT_SELECTOR}`;
  productsVersion: { [key: string]: string };
  releaseDate: Date;
  copyrightText: string;
  legalDocumentLink: string;
  showSpinner = false;
  versionInfo: VersionInfoDto;
  canExpire: boolean;
  enabledModulesSummary: string;
  moduleTranslateKeys: { [apiName: string]: string };
  constructor(
    private readonly _dialogRef: MatDialogRef<VersionInfoPopupComponent>,
    private readonly _versionInfoService: VersionInfoService,
    private readonly _objectHelperService: ObjectHelperService,
    private readonly _dateHelperService: DateHelperService
  ) {}

  ngOnInit(): void {
    this.loadVersionInfo();
  }

  private loadVersionInfo() {
    this.showSpinner = true;
    this._versionInfoService
      .getVersionCt()
      .pipe(untilDestroyed(this))
      .subscribe((versionInfo) => {
        const sorted = this._objectHelperService.sortObjectKeys(versionInfo.productsVersion);
        this.productsVersion = sorted as { [key: string]: string };
        this.releaseDate = this._dateHelperService.fromApiFormat(environment.releaseDate);
        this.copyrightText = environment.copyright;
        this.legalDocumentLink = environment.legalUrl;
        this.versionInfo = versionInfo;
        this.canExpire = this.checkCanExpire();
        this.buildModuleTranslateKeys();
        this.showSpinner = false;
      });
  }

  buildModuleTranslateKeys(): void {
    this.moduleTranslateKeys = this.versionInfo.enabledModules.reduce(
      (accum, currentModuleName) => {
        accum[currentModuleName] = this.getModuleTranslateKey(currentModuleName);
        return accum;
      },
      {}
    );
  }

  getModuleTranslateKey(moduleName: string): string {
    const module = cloudTwinLicenseModules.find((module) => module.apiName === moduleName);
    if (!module || !module.navKey) {
      console.error(`The module ${moduleName} does not have a frontend translation`);
      return moduleName.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    const moduleInHiddenLinks = cloudTwinAppNavigations.hiddenLinks.get(module.navKey);
    if (moduleInHiddenLinks) {
      return moduleInHiddenLinks.titleTranslateKey;
    }

    const moduleInLinks = cloudTwinAppNavigations.links.get(module.navKey);
    if (moduleInLinks) {
      return moduleInLinks.titleTranslateKey;
    }

    return moduleName.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  checkCanExpire = (): boolean =>
    typeof this.versionInfo.expiration == 'string' && !this.versionInfo.expiration.includes('9999');

  close() {
    this._dialogRef.close();
  }
}
